import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from 'providers/AuthProvider';
import WamiaLoader from 'components/common/WamiaLoader';

/**
 * Props for the PublicRoute component
 */
interface PublicRouteProps {
  children: React.ReactNode;
  /**
   * Optional minimum loading time in milliseconds
   * @default 300
   */
  minimumLoaderTime?: number;
  /**
   * Path to redirect authenticated users to
   * @default "/"
   */
  redirectPath?: string;
  /**
   * Determines if authenticated users should be redirected away from this route
   * @default true for authentication pages, false for other public pages
   */
  redirectAuthenticated?: boolean;
}

/**
 * PublicRoute is used for routes that are accessible to all users (logged-in or not)
 * By default, it redirects authenticated users away from authentication pages (like login/signup)
 * For non-authentication public pages, it allows access to all users regardless of authentication status
 */
const PublicRoute: React.FC<PublicRouteProps> = ({ 
  children, 
  minimumLoaderTime = 300,
  redirectPath = "/",
  redirectAuthenticated
}) => {
  const { isAuthenticated, profileLoading } = useAuth();
  const location = useLocation();
  const [showLoader, setShowLoader] = useState(true);
  const [minimumTimeElapsed, setMinimumTimeElapsed] = useState(false);
  
  // Determine if we're on an authentication page if redirectAuthenticated isn't explicitly provided
  const isAuthPage = location.pathname.includes("/sign-in") || 
                     location.pathname.includes("/sign-up") || 
                     location.pathname.includes("/authentication") ||
                     location.pathname.includes("/auth/");
  
  // Determine if we should redirect authenticated users away from this route
  const shouldRedirect = redirectAuthenticated !== undefined ? redirectAuthenticated : isAuthPage;

  // Ensure loader displays for the minimum time
  useEffect(() => {
    const timer = setTimeout(() => {
      setMinimumTimeElapsed(true);
    }, minimumLoaderTime);
    
    return () => clearTimeout(timer);
  }, [minimumLoaderTime]);
  
  // Hide loader when authentication state is determined and minimum time has passed
  useEffect(() => {
    if (minimumTimeElapsed && !profileLoading && isAuthenticated !== null) {
      setShowLoader(false);
    }
  }, [minimumTimeElapsed, profileLoading, isAuthenticated]);

  // Show loader during authentication checks or minimum display time
  if ((profileLoading || isAuthenticated === null || !minimumTimeElapsed) && showLoader) {
    return (
      <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center" 
           style={{ 
             zIndex: 99999, 
             backgroundColor: 'white'
           }}>
        <WamiaLoader />
      </div>
    );
  }

  // Redirect authenticated users if this is an auth page or redirectAuthenticated is set to true
  if (isAuthenticated && shouldRedirect) {
    return <Navigate to={redirectPath} />;
  }

  // Render children for all users
  return <>{children}</>;
};

export default PublicRoute;
